// Smart Daara - Teacher Performance Analytics
import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { analyticsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  BarChart3, Trophy, GraduationCap, BookOpen, Clock, Award,
  Loader2, Search, UserCog, TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';

export default function AnalyticsPage() {
  const { language } = useLanguage();
  const t = (fr, en) => (language === 'fr' ? fr : en);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await analyticsAPI.getTeacherPerformance();
        setTeachers(res.data || []);
      } catch (e) {
        toast.error(t('Erreur de chargement', 'Loading error'));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = teachers.filter((tt) => {
    const q = search.toLowerCase();
    const name = `${tt.first_name || ''} ${tt.last_name || ''}`.toLowerCase();
    return !q || name.includes(q) || (tt.email || '').toLowerCase().includes(q);
  });

  const top3 = teachers.slice(0, 3);
  const podiumColors = ['bg-amber-100 border-amber-300 text-amber-800', 'bg-slate-100 border-slate-300 text-slate-800', 'bg-orange-100 border-orange-300 text-orange-800'];
  const podiumMedals = ['🥇', '🥈', '🥉'];

  const displayName = (tt) => `${tt.first_name || ''} ${tt.last_name || ''}`.trim() || tt.email;

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div>
        <h1 className="text-2xl font-outfit font-bold text-slate-900 flex items-center gap-2">
          <BarChart3 className="w-6 h-6 text-emerald-600" />
          {t('Analytics — Performance des enseignants', 'Analytics — Teacher performance')}
        </h1>
        <p className="text-slate-500">
          {t(
            "Vue d'ensemble de l'activité et de l'impact de chaque enseignant.",
            'Overview of each teacher activity and impact.'
          )}
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      ) : teachers.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="text-center py-12 text-slate-500">
            <UserCog className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p>{t('Aucun enseignant à analyser pour le moment.', 'No teachers to analyze yet.')}</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Podium */}
          {top3.length > 0 && (
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="font-outfit flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-amber-600" />
                  {t('Top 3 par taux de complétion', 'Top 3 by completion rate')}
                </CardTitle>
                <CardDescription>
                  {t('Meilleurs profs selon la progression moyenne des élèves.', 'Top teachers by students average progression.')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-3 gap-4">
                  {top3.map((tt, i) => (
                    <div
                      key={tt.teacher_id}
                      className={`rounded-lg border-2 p-4 ${podiumColors[i]}`}
                      data-testid={`podium-${i}`}
                    >
                      <div className="text-3xl mb-2">{podiumMedals[i]}</div>
                      <div className="font-semibold text-slate-900 truncate">{displayName(tt)}</div>
                      <div className="text-xs text-slate-600 truncate">{tt.email}</div>
                      <div className="mt-3 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4" />
                        <span className="font-bold text-lg">{tt.completion_rate}%</span>
                        <span className="text-xs">{t('complétion', 'completion')}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Search */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder={t('Rechercher un enseignant…', 'Search a teacher…')}
                  className="pl-9"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="analytics-search"
                />
              </div>
            </CardContent>
          </Card>

          {/* Table */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="font-outfit">
                {t('Détails par enseignant', 'Details per teacher')}
              </CardTitle>
              <CardDescription>
                {filtered.length} {t('enseignant(s) analysé(s)', 'teacher(s) analyzed')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-slate-500">
                      <th className="py-3 px-3 font-medium">{t('Enseignant', 'Teacher')}</th>
                      <th className="py-3 px-3 font-medium">
                        <BookOpen className="w-4 h-4 inline mr-1" />
                        {t('Cours', 'Courses')}
                      </th>
                      <th className="py-3 px-3 font-medium">
                        {t('Leçons', 'Lessons')}
                      </th>
                      <th className="py-3 px-3 font-medium">
                        <GraduationCap className="w-4 h-4 inline mr-1" />
                        {t('Élèves', 'Students')}
                      </th>
                      <th className="py-3 px-3 font-medium">
                        <TrendingUp className="w-4 h-4 inline mr-1" />
                        {t('Complétion', 'Completion')}
                      </th>
                      <th className="py-3 px-3 font-medium">
                        <Award className="w-4 h-4 inline mr-1" />
                        {t('Score quiz', 'Quiz score')}
                      </th>
                      <th className="py-3 px-3 font-medium">
                        <Clock className="w-4 h-4 inline mr-1" />
                        {t('Temps visionné', 'Watch time')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((tt) => (
                      <tr key={tt.teacher_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`analytics-row-${tt.teacher_id}`}>
                        <td className="py-3 px-3">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center">
                              <UserCog className="w-4 h-4 text-amber-700" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{displayName(tt)}</p>
                              <p className="text-xs text-slate-500">{tt.email}</p>
                              {tt.specialization && (
                                <Badge variant="outline" className="mt-1 text-xs">{tt.specialization}</Badge>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-3 font-semibold text-slate-900">{tt.courses_count}</td>
                        <td className="py-3 px-3 text-slate-700">{tt.lessons_count}</td>
                        <td className="py-3 px-3 font-semibold text-slate-900">{tt.students_count}</td>
                        <td className="py-3 px-3">
                          <span
                            className={
                              tt.completion_rate >= 70 ? 'text-emerald-700 font-semibold'
                              : tt.completion_rate >= 40 ? 'text-amber-700 font-semibold'
                              : 'text-slate-600'
                            }
                          >
                            {tt.completion_rate}%
                          </span>
                        </td>
                        <td className="py-3 px-3">
                          <span className={tt.avg_quiz_score >= 70 ? 'text-emerald-700 font-semibold' : 'text-slate-700'}>
                            {tt.avg_quiz_score}/100
                          </span>
                        </td>
                        <td className="py-3 px-3 text-slate-700">{tt.total_watch_hours}h</td>
                      </tr>
                    ))}
                    {filtered.length === 0 && (
                      <tr>
                        <td colSpan={7} className="py-6 text-center text-slate-500">
                          {t('Aucun résultat', 'No results')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
